<script lang="ts" setup>
import { computed, useAttrs } from 'vue';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

defineOptions({
    name: 'IdeoHeading',
    inheritAttrs: false
});

const props = withDefaults(defineProps<{
    tag?: HeadingTag;
}>(), {
    tag: 'h2'
});

const attrs = useAttrs();

const sizeClasses: Record<HeadingTag, string> = {
    h1: 'text-[19px]',
    h2: 'text-[18px]',
    h3: 'text-[17px]',
    h4: 'text-[16px]',
    h5: 'text-[15px]',
    h6: 'text-[14px]'
};

const classTokens = (value: any): string[] =>
{
    if (!value)
        return [];

    if (typeof value === 'string')
        return value.split(/\s+/).filter(Boolean);

    if (Array.isArray(value))
        return value.flatMap(item => classTokens(item));

    if (typeof value === 'object')
        return Object.entries(value)
            .filter(([, active]) => active)
            .flatMap(([name]) => classTokens(name));

    return [];
};

const hasClass = (pattern: RegExp) =>
{
    return classTokens(attrs.class).some(item => pattern.test(item));
};

const rootAttrs = computed(() =>
{
    const result = { ...attrs };

    delete result.class;

    return result;
});

const headingClasses = computed(() => [
    !hasClass(/^(?:[\w-]+:)*text-(?:\[[^\]]+\]|\d?xl|xs|sm|base|lg)(?:\/[^\s]+)?$/) ? sizeClasses[props.tag] : '',
    !hasClass(/^(?:[\w-]+:)*font-(?:thin|extralight|light|normal|medium|semibold|bold|extrabold|black|\[[^\]]+\])$/) ? 'font-medium' : '',
    !hasClass(/^(?:[\w-]+:)*(?:m|my|mb)-/) ? 'mb-[5px]' : '',
    attrs.class
]);
</script>

<template>
    <component :is="props.tag" v-bind="rootAttrs" :class="headingClasses">
        <slot name="default" />
    </component>
</template>
